/**
 * Rating — star rating display and interactive picker.
 *
 * Supports half-star display, tap-to-rate with haptic feedback,
 * and optional review count label.
 *
 * @see docs/milestones/M0.6-UI-PRIMITIVES.md §15
 */

import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { View, Text, type AccessibilityActionEvent } from 'react-native'
import { Star } from '@/components/ui/icons'
import { selection } from '@/lib/utils/haptics'
import { Pressable } from './Pressable'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors, SPACING } from '../../lib/theme/tokens'
import { COLORS, FONTS } from '../../lib/brand'

// ─── Types ──────────────────────────────────────────────────────────────────

interface RatingProps {
  /** Current rating value (0.0 – 5.0) */
  value: number
  /** If provided, renders interactive star picker */
  onChange?: (value: number) => void
  /** Max stars (default: 5) */
  max?: number
  /** Star size (default: 20) */
  size?: number
  /** Show numeric label next to stars */
  showLabel?: boolean
  /** Number of reviews */
  count?: number
  /** Disabled interactive mode */
  disabled?: boolean
  /** Test id on the container; interactive stars get `${testID}.star-N` */
  testID?: string
}

// ─── Rating ─────────────────────────────────────────────────────────────────

export function Rating({
  value,
  onChange,
  max = 5,
  size = 20,
  showLabel = false,
  count,
  disabled = false,
  testID,
}: RatingProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const isInteractive = !!onChange && !disabled

  const handlePress = useCallback(
    (star: number) => {
      if (!isInteractive) return
      onChange?.(star)
      void selection()
    },
    [isInteractive, onChange],
  )

  const stars = Array.from({ length: max }, (_, i) => {
    const starNumber = i + 1
    const isFilled = value >= starNumber
    const isHalf = !isFilled && value >= starNumber - 0.5

    return (
      <StarIcon
        key={i}
        filled={isFilled}
        half={isHalf}
        size={size}
        starNumber={starNumber}
        interactive={isInteractive}
        onPress={() => handlePress(starNumber)}
        borderColor={colors.border}
        testID={testID ? `${testID}.star-${starNumber}` : undefined}
      />
    )
  })

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        opacity: onChange && disabled ? 0.4 : 1,
      }}
      accessibilityRole="adjustable"
      accessibilityLabel={t('a11y.rating', { value, max })}
      accessibilityValue={{ min: 0, max, now: value }}
      accessibilityActions={
        isInteractive
          ? [
              { name: 'increment', label: 'Increase rating' },
              { name: 'decrement', label: 'Decrease rating' },
            ]
          : undefined
      }
      onAccessibilityAction={
        isInteractive
          ? (e: AccessibilityActionEvent) => {
              if (e.nativeEvent.actionName === 'increment' && value < max) {
                onChange?.(Math.min(value + 1, max))
              } else if (e.nativeEvent.actionName === 'decrement' && value > 0) {
                onChange?.(Math.max(value - 1, 0))
              }
            }
          : undefined
      }
      testID={testID}
    >
      {stars}
      {showLabel && (
        <Text
          style={{
            marginLeft: SPACING.xs,
            fontSize: size * 0.7,
            fontFamily: FONTS.bodyBold,
            color: colors.textPrimary,
          }}
        >
          {Math.min(Math.max(value, 0), max).toFixed(1)}
        </Text>
      )}
      {count !== undefined && (
        <Text
          style={{
            marginLeft: SPACING.xs,
            fontSize: size * 0.6,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
          }}
        >
          ({count})
        </Text>
      )}
    </View>
  )
}

// ─── Star Icon ──────────────────────────────────────────────────────────────

function StarIcon({
  filled,
  half,
  size,
  starNumber,
  interactive,
  onPress,
  borderColor,
  testID,
}: {
  filled: boolean
  half: boolean
  size: number
  starNumber: number
  interactive: boolean
  onPress: () => void
  borderColor: string
  testID?: string
}) {
  const { t } = useTranslation()
  const fillColor = COLORS.amber.DEFAULT
  const emptyColor = borderColor

  // Half-star: clip a filled star to 50% width over an empty star
  const starContent = half ? (
    <View style={{ width: size, height: size, marginRight: 2 }}>
      <Star
        size={size}
        color={emptyColor}
        fill="transparent"
        strokeWidth={1.5}
        style={{ position: 'absolute' }}
      />
      <View style={{ position: 'absolute', width: size / 2, overflow: 'hidden' }}>
        <Star size={size} color={fillColor} fill={fillColor} strokeWidth={1.5} />
      </View>
    </View>
  ) : (
    <Star
      size={size}
      color={filled ? fillColor : emptyColor}
      fill={filled ? fillColor : 'transparent'}
      strokeWidth={1.5}
      style={{ marginRight: 2 }}
    />
  )

  if (interactive) {
    // WCAG: interactive stars pad out to a minimum 44pt touch target.
    return (
      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={t('ui.rate_star', { count: starNumber })}
        testID={testID}
        style={{
          minWidth: 44,
          minHeight: 44,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {starContent}
      </Pressable>
    )
  }

  return starContent
}
